import { mapApprover } from '@webapp/core/data-access/mappers/mapApprover'
import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { type ApproverDelegateeConstructor } from '@webapp/core/entities/internal/ApproverDelegatee'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import {
  EXPENSE_TYPE_NAME_BY_ENUM,
  ExpenseTypes,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseConstructor } from '@webapp/modules/expense/entities/Expense'
import { type ExpenseItemConstructor } from '@webapp/modules/expense/entities/ExpenseItem'
import { type ExpenseTypeData } from '@webapp/modules/expense/entities/ExpenseItem/ExpenseTypeData'
import { type NextApproverChoiceConstructor } from '@webapp/modules/expense/entities/NextApproverChoice'
import { type NextApproverChoiceApiModel } from '../api-models/ApprovableExpenseSummaryApiModel'
import {
  type ExpenseApiModel,
  type ExpensePermissionsApiModel,
  type ExpenseTypeFieldsApiValue,
} from '../api-models/ExpenseApiModel'

export const mapExpenseTypeData = (
  expenseType: ExpenseTypeEnum,
  expenseTypeFields: Record<string, ExpenseTypeFieldsApiValue>
): ExpenseTypeData => {
  const typeName = EXPENSE_TYPE_NAME_BY_ENUM[expenseType]

  if (typeName === 'MEALS') {
    const fields = expenseTypeFields.MEALS
    return {
      type: 'MEALS',
      data: { tips: Number.parseFloat(fields?.tips?.value ?? '0') },
    }
  }

  if (typeName === 'MILEAGE') {
    const fields = expenseTypeFields.MILEAGE
    return {
      type: 'MILEAGE',
      data: {
        mileageRate: Number.parseFloat(fields?.mileage_rate?.value ?? '0'),
        distance: Number.parseFloat(fields?.distance?.value ?? '0'),
        unitOfDistance: fields?.unit_of_distance?.value === 'mi' ? 'mi' : 'km',
        advisoryFuelRate: fields?.advisory_fuel_rate?.value
          ? Number.parseFloat(fields.advisory_fuel_rate.value)
          : undefined,
      },
    }
  }

  if (typeName === 'LABOUR') {
    const fields = expenseTypeFields.LABOUR
    return {
      type: 'LABOUR',
      data: {
        hourlyRate: Number.parseFloat(fields?.hourly_rate?.value ?? '0'),
        hoursOfWork: Number.parseFloat(fields?.hours_of_work?.value ?? '0'),
      },
    }
  }

  if (typeName === 'AIRFARE') {
    const fields = expenseTypeFields.AIRFARE
    return {
      type: 'AIRFARE',
      data: {
        ticketNumber: fields?.ticket_number?.value ?? '',
        arrivalDate: fields?.arrival_date?.value || null,
        departureDate: fields?.departure_date?.value || null,
        airline: fields?.airline?.value ?? '',
      },
    }
  }

  if (typeName === 'LODGING') {
    return { type: 'LODGING', data: null }
  }

  return { type: 'OTHER', data: null }
}

const mapNextApproverChoice = (
  choice: NextApproverChoiceApiModel
): NextApproverChoiceConstructor => {
  const delegatee = choice.approval_delegatee
  const delegateeConstructor: Nullable<ApproverDelegateeConstructor> = delegatee
    ? {
        id: delegatee.id,
        firstName: delegatee.firstName,
        lastName: delegatee.lastName,
        expectedReturnDate: choice.expected_return_date || null,
      }
    : null

  return {
    id: choice.id,
    firstName: choice.firstName,
    lastName: choice.lastName,
    levelText: choice.level_text || null,
    approvalDelegatee: delegateeConstructor,
  }
}

type ExpenseItemApiModel = ExpenseApiModel['items'][number]

const mapItemAccount = (
  account: ExpenseItemApiModel['account']
): ExpenseItemConstructor['account'] => ({
  id: account.id,
  accountCode: {
    id: account.account_code.id,
    description: account.account_code.description,
    code: account.account_code.code,
    type: account.account_code.account_type,
  },
  active: true,
  secondaryCategory: null,
})

const mapPaymentCard = (
  creditcard: ExpenseItemApiModel['creditcard']
): ExpenseItemConstructor['paymentCard'] =>
  creditcard
    ? {
        id: creditcard.id,
        name: creditcard.name,
        last4Digits: creditcard.number,
        vendor: creditcard.type,
      }
    : null

const mapExpenseItem = (item: ExpenseItemApiModel): ExpenseItemConstructor => ({
  id: item.id,
  merchant: item.merchant,
  amount: Number.parseFloat(item.amount),
  reimbursable: item.reimburse,
  taxAmount: Number.parseFloat(item.tax_amount),
  taxPercentage: item.tax_percentage,
  taxName: item.tax_name,
  taxId: item.tax_id,
  notes: item.notes || '',
  attachment: item.attachment || null,
  transactionDate: item.transDate,
  currency: mapCurrency(item.localCurrency),
  account: mapItemAccount(item.account),
  paymentCard: mapPaymentCard(item.creditcard),
  expenseType: item.expense_type ?? ExpenseTypes.OTHER,
  paymentType: item.paymentType ?? ExpensePaymentTypes.CASH,
  expenseTypeData: mapExpenseTypeData(
    item.expense_type ?? ExpenseTypes.OTHER,
    item.expense_type_fields ?? {}
  ),
  mileageRatePresetId: item.mileage_preset_id ?? null,
  mileageRatePresetName: item.mileage_preset_name ?? null,
})

export const mapExpenseApiModel = (
  apiModel: ExpenseApiModel,
  permissions: ExpensePermissionsApiModel
): ExpenseConstructor => ({
  id: apiModel.id,
  uuid: apiModel.uuid,
  title: apiModel.title,
  status: apiModel.status,
  description: apiModel.notes,
  submittedDate: apiModel.createdDate,
  dateLastModified: apiModel.dateLastModified,
  currency: mapCurrency(apiModel.currency),
  requester: {
    id: apiModel.user.id,
    firstName: apiModel.user.firstName,
    lastName: apiModel.user.lastName,
    profileImage: apiModel.user.profile_image,
    primaryCategory: {
      id: apiModel.department.branch.id,
      name: apiModel.department.branch.name,
    },
    secondaryCategory: {
      id: apiModel.department.id,
      name: apiModel.department.name,
      primaryCategory: {
        id: apiModel.department.branch.id,
        name: apiModel.department.branch.name,
      },
    },
  },
  primaryCategory: {
    id: apiModel.department.branch.id,
    name: apiModel.department.branch.name,
  },
  secondaryCategory: {
    id: apiModel.department.id,
    name: apiModel.department.name,
    primaryCategory: {
      id: apiModel.department.branch.id,
      name: apiModel.department.branch.name,
    },
  },
  nextApprover: apiModel.next_approver
    ? mapApprover(apiModel.next_approver)
    : null,
  nextApproverChoices: (apiModel.next_approver_choices || []).map(
    mapNextApproverChoice
  ),
  items: apiModel.items.map(mapExpenseItem),
  logs: apiModel.logs.map((log) => ({
    id: log.id,
    action: log.action,
    dateTime: log.dateTime,
    user: {
      id: log.user.id,
      firstName: log.user.firstName,
      lastName: log.user.lastName,
      profileImage: '',
      approvalDelegatee: null,
      primaryCategory: null,
      secondaryCategory: null,
      triggersFinalApproval: null,
      groupName: undefined,
    },
  })),
  totalAmount: apiModel.total_amount,
  reimbursableAmount:
    typeof apiModel.reimbursable_amount === 'string'
      ? Number.parseFloat(apiModel.reimbursable_amount)
      : apiModel.reimbursable_amount,
  permissions: {
    canEdit: permissions.can_edit,
    canDelete: permissions.can_delete,
    canApprove: permissions.can_approve,
    canRevise: permissions.can_revise,
  },
  travel: apiModel.travel
    ? {
        id: apiModel.travel.id,
        uuid: apiModel.travel.uuid,
        purpose: apiModel.travel.purpose,
      }
    : null,
  approvalDelegatee: apiModel.approval_delegatee || null,
  commentsCount: apiModel.comments_count ?? 0,
})
